import { useState } from 'react';
import type { EnrollmentTokenView } from '@teachpartner/contracts';
import { describeError } from '../../api/client';
import { useServices } from '../../app-context';
import { ErrorBanner } from '../../components/badges';

interface Props {
  readonly code: string;
  readonly token: EnrollmentTokenView | null;
  readonly enrolledAt: string | null;
  readonly bootstrapAdminEmail: string | null;
  onChanged(): void;
}

/** Who becomes the first Tenant Admin (only before enrollment; central then deletes it). */
function FirstAdmin({ email }: { email: string | null }) {
  if (!email) {
    return (
      <p className="banner banner-warn">
        No first Tenant Admin set: add one in Settings before the school enrolls.
      </p>
    );
  }
  return (
    <p>
      First Tenant Admin: <strong>{email}</strong> (sent to the school with its identity, then
      deleted from central when the school enrolls).
    </p>
  );
}

/** Issue / revoke the one-time enrollment token. The plaintext is shown exactly once. */
export function EnrollmentSection({
  code,
  token,
  enrolledAt,
  bootstrapAdminEmail,
  onChanged,
}: Props) {
  const { api } = useServices();
  const [issued, setIssued] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const act = async (fn: () => Promise<void>) => {
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setError(describeError(e));
    }
  };

  return (
    <section aria-label="Enrollment">
      <h2>Enrollment</h2>
      <ErrorBanner message={error} />
      <p>
        {enrolledAt ? `School enrolled ${enrolledAt}.` : 'School not enrolled yet.'}{' '}
        {token ? `Latest token: ${token.state} (expires ${token.expiresAt}).` : 'No token issued.'}
      </p>
      {!enrolledAt && <FirstAdmin email={bootstrapAdminEmail} />}
      {issued && (
        <div className="banner banner-warn" role="status">
          <p>
            Give this token to the school&apos;s installer now (install.sh asks for it; the school
            gets its code, name, hostnames and first Tenant Admin from central). It will not be
            shown again.
          </p>
          <code data-testid="enrollment-token">{issued}</code>
        </div>
      )}
      <button
        type="button"
        onClick={() =>
          void act(async () => setIssued((await api.issueEnrollmentToken(code)).token))
        }
      >
        {token?.state === 'active' ? 'Issue new token (revokes the current one)' : 'Issue token'}
      </button>
      {token?.state === 'active' && (
        <button
          type="button"
          className="danger"
          onClick={() =>
            void act(async () => {
              await api.revokeEnrollmentToken(code);
              setIssued(null);
            })
          }
        >
          Revoke token
        </button>
      )}
    </section>
  );
}
